/**
 * FilterPanel Component
 * Advanced filtering panel for OKR list page
 */

import React, { useState, useEffect } from 'react';
import { X, Filter, RotateCcw, Bookmark } from 'lucide-react';
import type { OKRFilters, FilterMetadata } from '../../types/okr';
import { okrService } from '../../services/okr.service';
import { useFilterHistory } from '../../hooks/useFilterHistory';
import {
  SearchFilter,
  TypeFilter,
  StatusFilter,
  OwnerFilter,
  DepartmentFilter,
  CycleFilter,
  ProgressFilter,
  DateFilter,
} from './filters';
import { FilterPresetManager } from './FilterPresetManager';
import { QuickFilters } from './QuickFilters';
import { FilterHistory } from './FilterHistory';

interface FilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  filters: OKRFilters;
  onFiltersChange: (filters: OKRFilters) => void;
  onApply: () => void;
  onClear: () => void;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onApply,
  onClear,
}) => {
  const [metadata, setMetadata] = useState<FilterMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'filters' | 'presets'>('filters');
  const { addToHistory } = useFilterHistory();

  // Fetch filter metadata on mount
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        setLoading(true);
        const data = await okrService.getFilterMetadata();
        setMetadata(data);
      } catch (error) {
        console.error('Failed to fetch filter metadata:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchMetadata();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-30 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="border-b border-stone-200 bg-gradient-to-r from-stone-800 via-stone-700 to-primary-600">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-2 text-white">
                <Filter className="w-5 h-5" />
                <h2 className="text-lg font-semibold">Advanced Filters</h2>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white hover:bg-opacity-20 p-1.5 rounded-lg transition-colors"
                title="Close filters"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-6">
              <button
                onClick={() => setActiveTab('filters')}
                className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors ${
                  activeTab === 'filters'
                    ? 'bg-white text-primary-600 rounded-t-lg'
                    : 'text-white hover:text-stone-200'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
              <button
                onClick={() => setActiveTab('presets')}
                className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors ${
                  activeTab === 'presets'
                    ? 'bg-white text-primary-600 rounded-t-lg'
                    : 'text-white hover:text-stone-200'
                }`}
              >
                <Bookmark className="w-4 h-4" />
                Saved Presets
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {activeTab === 'presets' ? (
              <div className="space-y-6">
                <FilterPresetManager
                  currentFilters={filters}
                  onLoadPreset={(loadedFilters) => {
                    onFiltersChange(loadedFilters);
                    setActiveTab('filters');
                  }}
                />

                {/* Divider */}
                <div className="border-t border-stone-200" />

                <FilterHistory
                  onRestoreFilters={(restoredFilters) => {
                    onFiltersChange(restoredFilters);
                    setActiveTab('filters');
                  }}
                />
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : metadata ? (
              <div className="space-y-6">
                {/* Quick Filters */}
                <QuickFilters
                  onApplyQuickFilter={(quickFilters) => {
                    onFiltersChange({ ...filters, ...quickFilters });
                  }}
                  metadata={metadata}
                />

                {/* Divider */}
                <div className="border-t border-stone-200" />

                {/* Search Filter */}
                <SearchFilter
                  value={filters.search || ''}
                  onChange={(value) => onFiltersChange({ ...filters, search: value })}
                />

                {/* Divider */}
                <div className="border-t border-stone-200" />

                {/* Filter Combination Mode */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-stone-700">
                    Filter Combination Mode
                  </label>
                  <div className="flex items-center gap-2 p-3 bg-stone-50 rounded-lg">
                    <button
                      onClick={() => onFiltersChange({ ...filters, filterMode: 'AND' })}
                      className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        (filters.filterMode || 'AND') === 'AND'
                          ? 'bg-primary-600 text-white shadow-sm'
                          : 'bg-white text-stone-700 hover:bg-stone-100 border border-stone-300'
                      }`}
                    >
                      AND
                    </button>
                    <button
                      onClick={() => onFiltersChange({ ...filters, filterMode: 'OR' })}
                      className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        filters.filterMode === 'OR'
                          ? 'bg-primary-600 text-white shadow-sm'
                          : 'bg-white text-stone-700 hover:bg-stone-100 border border-stone-300'
                      }`}
                    >
                      OR
                    </button>
                  </div>
                  <p className="text-xs text-stone-500">
                    {(filters.filterMode || 'AND') === 'AND'
                      ? 'Match ALL selected criteria (more restrictive)'
                      : 'Match ANY selected criteria (more permissive)'}
                  </p>
                </div>

                {/* Divider */}
                <div className="border-t border-stone-200" />

                {/* Type Filter */}
                <TypeFilter
                  availableTypes={metadata.types}
                  selectedTypes={filters.types || []}
                  onChange={(types) => onFiltersChange({ ...filters, types })}
                />

                {/* Divider */}
                <div className="border-t border-stone-200" />

                {/* Status Filter */}
                <StatusFilter
                  availableStatuses={metadata.statuses}
                  selectedStatuses={filters.statuses || []}
                  onChange={(statuses) => onFiltersChange({ ...filters, statuses })}
                />

                {/* Divider */}
                <div className="border-t border-stone-200" />

                {/* Owner Filter */}
                <OwnerFilter
                  availableOwners={metadata.owners}
                  selectedOwnerIds={filters.ownerIds || []}
                  onChange={(ownerIds) => onFiltersChange({ ...filters, ownerIds })}
                />

                {/* Divider */}
                <div className="border-t border-stone-200" />

                {/* Department Filter */}
                {metadata.departments.length > 0 && (
                  <>
                    <DepartmentFilter
                      availableDepartments={metadata.departments}
                      selectedDepartments={filters.departmentIds || []}
                      onChange={(departmentIds) => onFiltersChange({ ...filters, departmentIds })}
                    />
                    <div className="border-t border-stone-200" />
                  </>
                )}

                {/* Cycle Filter */}
                {metadata.cycles && metadata.cycles.length > 0 && (
                  <>
                    <CycleFilter
                      availableCycles={metadata.cycles}
                      selectedCycleIds={filters.cycleIds || []}
                      onChange={(cycleIds) => onFiltersChange({ ...filters, cycleIds })}
                    />
                    <div className="border-t border-stone-200" />
                  </>
                )}

                {/* Progress Filter */}
                <ProgressFilter
                  minValue={filters.progressMin ?? 0}
                  maxValue={filters.progressMax ?? 100}
                  onChange={(min, max) =>
                    onFiltersChange({ ...filters, progressMin: min, progressMax: max })
                  }
                  availableRange={metadata.progressRange}
                />

                {/* Divider */}
                <div className="border-t border-stone-200" />

                {/* Date Filter */}
                <DateFilter
                  startDate={filters.startDate || ''}
                  endDate={filters.endDate || ''}
                  onChange={(startDate, endDate) =>
                    onFiltersChange({ ...filters, startDate, endDate })
                  }
                  availableRange={metadata.dateRange}
                  selectedCycles={
                    filters.cycleIds && filters.cycleIds.length > 0 && metadata.cycles
                      ? metadata.cycles.filter((c) => filters.cycleIds?.includes(c.id))
                      : undefined
                  }
                />
              </div>
            ) : (
              <div className="text-sm text-stone-500 text-center py-8">
                Failed to load filter options
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-stone-200 bg-stone-50">
            <div className="flex items-center gap-3">
              <button
                onClick={onClear}
                className="flex-1 px-4 py-2.5 border-2 border-stone-300 text-stone-700 rounded-lg hover:bg-stone-100 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Clear All
              </button>
              <button
                onClick={() => {
                  addToHistory(filters);
                  onApply();
                  onClose();
                }}
                className="flex-1 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium shadow-md"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
